import { useEffect, useRef, useMemo, useState, useCallback } from "react";
import { Box, Flex, Text, Button, Spinner, Skeleton } from "@chakra-ui/react";
import Map, { NavigationControl, MapRef, Marker } from "react-map-gl/maplibre";
import Supercluster from "supercluster";
import AssetMarker from "./AssetMarker";
import ClusterMarker from "./ClusterMarker";
import AssetPopup from "./AssetPopup";
import { AssetMapItem } from "@hydrologiq/gql-experience/graphql";
import "./AlertsMapView.scss";

// Custom hook for debounced callbacks
function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): T {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  return useCallback((...args: Parameters<T>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]) as T;
}
// Free OpenStreetMap-based tiles (no API key required)
const MAP_STYLE =
  "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";

// Initial viewport for the map
const INITIAL_VIEW_STATE = {
  latitude: 54.97,
  longitude: -2.45,
  zoom: 6,
};

interface MapBounds {
  minLat: number;
  maxLat: number;
  minLong: number;
  maxLong: number;
  centerLat: number;
  centerLong: number;
}

interface AlertsMapViewProps {
  assets: AssetMapItem[];
  mapBounds?: MapBounds | null;
  loading: boolean;
  isRefetching?: boolean;
  error?: string;
  onRetry?: () => void;
}

function AlertsMapView({
  assets,
  mapBounds,
  loading,
  isRefetching = false,
  error,
  onRetry,
}: AlertsMapViewProps) {
  const mapRef = useRef<MapRef>(null);
  const [selectedAsset, setSelectedAsset] = useState<AssetMapItem | null>(null);

  useEffect(() => {
    if (selectedAsset) {
      const asset = assets.find((asset) => asset.id === selectedAsset?.id);
      if (!asset) {
        return;
      }
      const buildKey = (asset: AssetMapItem) => {
        return asset.alerts?.items?.map((alert) => {
          return alert.status + alert.id + "-";
        }).join("-");
      }
      if (buildKey(asset) !== buildKey(selectedAsset)) setSelectedAsset(asset);
    }
  }, [selectedAsset, assets]);
  const [expandedCluster, setExpandedCluster] = useState<any>(null);
  const [spiderfiedAssets, setSpiderfiedAssets] = useState<
    Array<AssetMapItem & { spiderLat: number; spiderLong: number }>
  >([]);
  const [isSpiderfying, setIsSpiderfying] = useState(false);

  // Track clusters separately - only update when map stops moving
  const [clusters, setClusters] = useState<any[]>([]);

  // Track which supercluster version these clusters belong to
  const superclusterVersionRef = useRef(0);

  // Create supercluster instance (memoized based on assets only)
  const supercluster = useMemo(() => {
    const validAssets = assets.filter(
      (asset) => asset.currentLocation?.lat && asset.currentLocation?.long
    );

    const points = validAssets.map((asset) => ({
      type: "Feature" as const,
      geometry: {
        type: "Point" as const,
        coordinates: [
          asset.currentLocation!.long!,
          asset.currentLocation!.lat!,
        ],
      },
      properties: {
        cluster: false,
        assetId: asset.id,
        asset,
      },
    }));

    const sc = new Supercluster({
      radius: 40,
      maxZoom: 16,
    });

    sc.load(points);

    // Increment version to track supercluster changes
    superclusterVersionRef.current += 1;

    return sc;
  }, [assets]);

  // Update clusters when supercluster changes (assets changed) or on initial load
  useEffect(() => {
    if (!supercluster) return;

    // Get current map bounds and zoom, or use defaults
    const bounds = mapRef.current?.getBounds();
    const zoom = mapRef.current ? Math.floor(mapRef.current.getZoom()) : INITIAL_VIEW_STATE.zoom;

    let newClusters;
    if (bounds) {
      newClusters = supercluster.getClusters(
        [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
        zoom
      );
    } else {
      // No bounds yet (initial load), use world bounds
      newClusters = supercluster.getClusters([-180, -85, 180, 85], zoom);
    }

    setClusters(newClusters);
  }, [supercluster]);

  // Function to update clusters based on current map view
  const updateClustersImmediate = useCallback(() => {
    if (!mapRef.current || !supercluster) return;

    const bounds = mapRef.current.getBounds();
    const zoom = Math.floor(mapRef.current.getZoom());

    if (bounds) {
      // Add buffer zone around viewport (20% padding)
      const latPadding = (bounds.getNorth() - bounds.getSouth()) * 0.2;
      const lngPadding = (bounds.getEast() - bounds.getWest()) * 0.2;

      const bufferedBounds: [number, number, number, number] = [
        bounds.getWest() - lngPadding,
        bounds.getSouth() - latPadding,
        bounds.getEast() + lngPadding,
        bounds.getNorth() + latPadding,
      ];

      const newClusters = supercluster.getClusters(bufferedBounds, zoom);
      setClusters(newClusters);
    }
  }, [supercluster]);

  // Debounced version for map interactions
  const updateClusters = useDebouncedCallback(updateClustersImmediate, 150);

  // Function to trigger immediate cluster update with loading state
  const triggerClusterUpdate = useCallback(() => {
    updateClusters();
  }, [updateClusters]);

  // Memoized viewport-filtered clusters for rendering optimization
  const visibleClusters = useMemo(() => {
    if (!mapRef.current || clusters.length === 0) return clusters;

    const bounds = mapRef.current.getBounds();
    if (!bounds) return clusters;

    // Filter clusters to only those in or near the viewport
    return clusters.filter((cluster) => {
      const [lng, lat] = cluster.geometry.coordinates;

      // Add small buffer for smooth transitions
      const latBuffer = (bounds.getNorth() - bounds.getSouth()) * 0.1;
      const lngBuffer = (bounds.getEast() - bounds.getWest()) * 0.1;

      return (
        lng >= bounds.getWest() - lngBuffer &&
        lng <= bounds.getEast() + lngBuffer &&
        lat >= bounds.getSouth() - latBuffer &&
        lat <= bounds.getNorth() + latBuffer
      );
    });
  }, [clusters]);

  // Helper function to create spiderfy positions for overlapping assets
  const createSpiderfyPositions = useCallback((
    centerLat: number,
    centerLong: number,
    assets: AssetMapItem[],
    radius = 0.001
  ) => {
    if (assets.length <= 1)
      return assets.map((asset) => ({
        ...asset,
        spiderLat: centerLat,
        spiderLong: centerLong,
      }));

    const angleStep = (2 * Math.PI) / assets.length;
    return assets.map((asset, index) => {
      const angle = index * angleStep;
      const spiderLat = centerLat + radius * Math.cos(angle);
      const spiderLong = centerLong + radius * Math.sin(angle);
      return { ...asset, spiderLat, spiderLong };
    });
  }, []);

  // Async spiderfy function using requestAnimationFrame for smooth performance
  const createSpiderfyAsync = useCallback((
    centerLat: number,
    centerLong: number,
    assets: AssetMapItem[],
    radius = 0.001
  ) => {
    setIsSpiderfying(true);

    requestAnimationFrame(() => {
      const spiderfied = createSpiderfyPositions(centerLat, centerLong, assets, radius);
      setSpiderfiedAssets(spiderfied);
      setExpandedCluster(null);
      setIsSpiderfying(false);
    });
  }, [createSpiderfyPositions]);

  // Helper function to find overlapping assets at the same location
  const findOverlappingAssets = (
    targetAsset: AssetMapItem,
    allAssets: AssetMapItem[],
    tolerance = 0.0001
  ) => {
    if (!targetAsset.currentLocation?.lat || !targetAsset.currentLocation?.long)
      return [targetAsset];

    return allAssets.filter((asset) => {
      if (!asset.currentLocation?.lat || !asset.currentLocation?.long)
        return false;

      const latDiff = Math.abs(
        asset.currentLocation.lat - targetAsset.currentLocation!.lat!
      );
      const longDiff = Math.abs(
        asset.currentLocation.long - targetAsset.currentLocation!.long!
      );

      return latDiff < tolerance && longDiff < tolerance;
    });
  };

  // Auto-center on assets when bounds are available
  useEffect(() => {
    if (mapRef.current && mapBounds && assets.length > 0) {
      mapRef.current.fitBounds(
        [
          [mapBounds.minLong, mapBounds.minLat],
          [mapBounds.maxLong, mapBounds.maxLat],
        ],
        { padding: 50, maxZoom: 12 }
      );
    }
  }, [mapBounds, assets.length]);

  // Clear spiderfy and expanded cluster when map moves
  const clearSpiderfy = useCallback(() => {
    if (spiderfiedAssets.length > 0 || expandedCluster) {
      setSpiderfiedAssets([]);
      setExpandedCluster(null);
    }
  }, [spiderfiedAssets.length, expandedCluster]);

  const handleClusterClick = (cluster: any) => {
    if (!mapRef.current || !supercluster) return;

    // Get the assets in this cluster - use try-catch as cluster IDs can become stale
    let clusterAssets: AssetMapItem[] = [];
    let expansionZoom = 16;
    try {
      clusterAssets = supercluster
        .getLeaves(cluster.id, Infinity)
        .map((leaf: any) => leaf.properties.asset);
      expansionZoom = Math.min(
        supercluster.getClusterExpansionZoom(cluster.id),
        16
      );
    } catch {
      // Cluster ID is stale, just zoom to the location
      mapRef.current.easeTo({
        center: cluster.geometry.coordinates,
        zoom: Math.min(mapRef.current.getZoom() + 2, 16),
        duration: 500,
      });
      return;
    }

    const currentZoom = mapRef.current.getZoom();

    // If cluster has few assets (<=8), show them in a circle instead of zooming
    if (clusterAssets.length <= 8 && currentZoom >= 10) {
      const [centerLong, centerLat] = cluster.geometry.coordinates;
      setExpandedCluster(cluster);
      createSpiderfyAsync(centerLat, centerLong, clusterAssets, 0.002);
      return;
    }

    // Otherwise, zoom in to expand the cluster
    mapRef.current.easeTo({
      center: cluster.geometry.coordinates,
      zoom: expansionZoom,
      duration: 500,
    });
  };

  const handleAssetClick = (asset: AssetMapItem) => {
    // Check if there are overlapping assets at this location
    const overlappingAssets = findOverlappingAssets(asset, assets);

    if (overlappingAssets.length > 1 && spiderfiedAssets.length === 0) {
      // Multiple assets at same location - spiderfy them
      createSpiderfyAsync(
        asset.currentLocation!.lat!,
        asset.currentLocation!.long!,
        overlappingAssets
      );
    } else {
      // Single asset or already spiderfied - show popup
      setSelectedAsset(asset);
    }
  };

  if (error) {
    return (
      <Flex
        height="100%"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        gap={4}
        bg="gray.50"
      >
        <Text color="red.600" fontSize="lg" fontWeight="600">
          Failed to load map data
        </Text>
        <Text color="gray.600" fontSize="sm">
          {error}
        </Text>
        {onRetry && (
          <Button onClick={onRetry} colorPalette="blue">
            Retry
          </Button>
        )}
      </Flex>
    );
  }

  return (
    <Box position="relative" height="100%" width="100%">
      {loading && (
        <Skeleton height="100%" width="100%" />
      )}

      {/* Refetch loading overlay */}
      {isRefetching && !loading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="blackAlpha.300"
          zIndex={1000}
          display="flex"
          alignItems="center"
          justifyContent="center"
          transition="opacity 0.2s ease-in-out"
        >
          <Box
            bg="white"
            borderRadius="md"
            p={4}
            boxShadow="lg"
            display="flex"
            alignItems="center"
            gap={3}
          >
            <Spinner size="sm" color="blue.500" />
            <Text fontSize="sm" fontWeight="medium" color="gray.700">
              Updating map...
            </Text>
          </Box>
        </Box>
      )}

      {/* Spiderfy loading indicator */}
      {isSpiderfying && (
        <Box
          position="absolute"
          top={4}
          right={4}
          bg="white"
          borderRadius="md"
          p={2}
          boxShadow="md"
          zIndex={999}
          display="flex"
          alignItems="center"
          gap={2}
        >
          <Spinner size="xs" color="blue.500" />
          <Text fontSize="xs" color="gray.600">
            Expanding...
          </Text>
        </Box>
      )}

      <Map
        ref={mapRef}
        initialViewState={INITIAL_VIEW_STATE}
        onMoveStart={clearSpiderfy}
        onMoveEnd={triggerClusterUpdate}
        onLoad={updateClustersImmediate}
        style={{ width: "100%", height: "100%" }}
        mapStyle={MAP_STYLE}
        onClick={() => {
          setSelectedAsset(null);
          setSpiderfiedAssets([]);
          setExpandedCluster(null);
        }}
      >
        <NavigationControl position="bottom-right" />

        {/* Render clusters and markers */}
        {visibleClusters.map((cluster: any) => {
          const [longitude, latitude] = cluster.geometry.coordinates;
          const { cluster: isCluster, point_count: pointCount } =
            cluster.properties;

          if (isCluster) {
            // Don't render cluster if it's expanded and showing spiderfied assets
            if (expandedCluster && expandedCluster.id === cluster.id) {
              return null;
            }

            // Calculate highest severity in cluster
            // Use try-catch as cluster IDs can become stale during rapid updates
            let clusterAssets: AssetMapItem[] = [];
            try {
              clusterAssets = supercluster
                .getLeaves(cluster.id, Infinity)
                .map((leaf: any) => leaf.properties.asset);
            } catch {
              // Cluster ID is stale, skip this cluster
              return null;
            }

            const severityPriority = {
              critical: 3,
              warning: 2,
              info: 1,
              healthy: 0,
            };
            const highestSeverity = clusterAssets.reduce(
              (highest: string | null, asset: AssetMapItem) => {
                const assetSeverity =
                  asset.alerts?.highestSeverity || "healthy";
                if (!highest) return assetSeverity;

                const currentPriority =
                  severityPriority[
                  assetSeverity as keyof typeof severityPriority
                  ] || 0;
                const highestPriority =
                  severityPriority[highest as keyof typeof severityPriority] ||
                  0;

                return currentPriority > highestPriority
                  ? assetSeverity
                  : highest;
              },
              null
            );

            return (
              <ClusterMarker
                key={`cluster-${cluster.id}`}
                latitude={latitude}
                longitude={longitude}
                pointCount={pointCount}
                highestSeverity={highestSeverity}
                onClick={() => handleClusterClick(cluster)}
              />
            );
          }

          const asset = cluster.properties.asset as AssetMapItem;

          // Don't render individual asset if it's part of spiderfied assets
          if (
            spiderfiedAssets.some((spiderAsset) => spiderAsset.id === asset.id)
          ) {
            return null;
          }

          return (
            <AssetMarker
              key={asset.id}
              asset={asset}
              latitude={latitude}
              longitude={longitude}
              onClick={() => handleAssetClick(asset)}
              isSelected={selectedAsset?.id === asset.id}
            />
          );
        })}

        {/* Render center marker for spiderfied assets */}
        {spiderfiedAssets.length > 0 &&
          (() => {
            const firstAsset = assets.find(
              (a) => a.id === spiderfiedAssets[0].id
            );
            if (
              !firstAsset?.currentLocation?.lat ||
              !firstAsset?.currentLocation?.long
            )
              return null;

            return (
              <Marker
                latitude={firstAsset.currentLocation.lat}
                longitude={firstAsset.currentLocation.long}
              >
                <Box
                  width="16px"
                  height="16px"
                  borderRadius="50%"
                  bg="gray.400"
                  border="2px solid white"
                  boxShadow="0 2px 4px rgba(0,0,0,0.2)"
                />
              </Marker>
            );
          })()}

        {/* Render spiderfied assets */}
        {spiderfiedAssets.map((asset) => (
          <AssetMarker
            key={`spider-${asset.id}`}
            asset={asset}
            latitude={asset.spiderLat}
            longitude={asset.spiderLong}
            onClick={() => handleAssetClick(asset)}
            isSelected={selectedAsset?.id === asset.id}
            isSpiderfied={true}
          />
        ))}

        {/* Asset popup */}
        {selectedAsset && selectedAsset.currentLocation && (
          <AssetPopup
            asset={selectedAsset}
            latitude={selectedAsset.currentLocation.lat!}
            longitude={selectedAsset.currentLocation.long!}
            onClose={() => setSelectedAsset(null)}
          />
        )}
      </Map>
    </Box>
  );
}

export default AlertsMapView;
